// EmpLeaves.js
import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Table,
  Spin,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Space,
  Tag,
} from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { Button } from "antd";
import EmployeeProfile from "../EmployeeProfile";


const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function EmpLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authData, backUrl } = useContext(AuthContext);

  const [selectedId, setSelectedId] = useState(null)
  const [showEmp, setShowEmp] = useState(false)

  const [Organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const onBack = () => {
    setShowEmp(false);
  };

  // New state to track if we're viewing rejected leaves
  const [viewingRejected, setViewingRejected] = useState(false);

  // Filters state
  const [filters, setFilters] = useState({
    name: "",
    emp_code: "",
    leave_type: "",
    status: "",
    dateRange: [],
    organisation: "", // expected to store organisation id (or name if only names exist)
    department: "",
    designation: "",
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  // Bulk Action States
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRemarks, setBulkRemarks] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // ---------- Helper accessors (defensive) ----------
  const getEmployeeFieldId = (employee, fieldBase) => {
    // Tries employee[fieldBase], employee[`${fieldBase}_id`], or null
    if (!employee) return null;
    if (employee[fieldBase] !== undefined && employee[fieldBase] !== null) {
      return employee[fieldBase];
    }
    const alt = `${fieldBase}_id`;
    if (employee[alt] !== undefined && employee[alt] !== null) return employee[alt];
    return null;
  };

  const getEmployeeFieldName = (employee, nameFieldCandidates = []) => {
    // nameFieldCandidates e.g. ["organisation_name", "organisation"]
    if (!employee) return "";
    for (const k of nameFieldCandidates) {
      if (employee[k]) return employee[k];
    }
    return "";
  };

  // ---------- Fetching organisations / departments / designations ----------
  const fetchOrganisation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      const results = res.data?.results ?? res.data ?? [];
      setOrganisations(results);
    } catch (err) {
      console.error("Error fetching organisations:", err);
      toast.error("Failed to load organisations");
    }
  };

  const fetchDepartments = async (organisationId) => {
    if (!organisationId) {
      setDepartments([]);
      return;
    }
    try {
      // API expects organisation query param
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${organisationId}`,
        {
          headers: { Authorization: `Bearer ${authData?.access}` },
        }
      );
      const results = res.data?.results ?? res.data ?? [];
      setDepartments(results);
    } catch (err) {
      console.error("Error fetching departments:", err);
      toast.error("Failed to load departments");
    }
  };

  const fetchDesignations = async (departmentId) => {
    if (!departmentId) {
      setDesignations([]);
      return;
    }
    try {
      // API expects department query param
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${departmentId}`,
        {
          headers: { Authorization: `Bearer ${authData?.access}` },
        }
      );
      const results = res.data?.results ?? res.data ?? [];
      setDesignations(results);
    } catch (err) {
      console.error("Error fetching designations:", err);
      toast.error("Failed to load designations");
    }
  };

  // ---------- Fetch leaves ----------
  const fetchLeaves = async () => {
    setLoading(true);
    setViewingRejected(false);
    try {
      const res = await axios.get(`${backUrl}/api/hr/leaves/`, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });
      const data = res.data?.results ?? res.data ?? [];
      setLeaves(data);
      setFilteredLeaves(data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
      toast.error("Failed to load leave data");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fetch rejected leaves ----------
  const fetchRejectedLeaves = async () => {
    setLoading(true);
    setViewingRejected(true);
    try {
      const res = await axios.get(`${backUrl}/api/hr/leaves/actioned-rejected/`, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });
      const data = res.data?.results ?? res.data ?? [];
      setLeaves(data);
      setFilteredLeaves(data);
    } catch (error) {
      console.error("Error fetching rejected leaves:", error);
      toast.error("Failed to load rejected leaves");
    } finally {
      setLoading(false);
    }
  };

  // initial loads
  useEffect(() => {
    fetchOrganisation();
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When organisation filter changes, fetch departments for that org
  useEffect(() => {
    const orgValue = filters.organisation;
    if (orgValue) {
      fetchDepartments(orgValue);
    } else {
      setDepartments([]);
      setDesignations([]);
      // Also reset dependent filters
      setFilters((prev) => ({ ...prev, department: "", designation: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.organisation]);

  // When department filter changes, fetch designations
  useEffect(() => {
    const deptValue = filters.department;
    if (deptValue) {
      fetchDesignations(deptValue);
    } else {
      setDesignations([]);
      setFilters((prev) => ({ ...prev, designation: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.department]);

  // ---------- Filtering logic ----------
  useEffect(() => {
    let result = [...leaves];

    if (filters.name) {
      const q = filters.name.toLowerCase();
      result = result.filter((item) =>
        `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""}`
          .toLowerCase()
          .includes(q)
      );
    }

    if (filters.emp_code) {
      const q = filters.emp_code.toLowerCase();
      result = result.filter((item) =>
        (item?.employee?.emp_code ?? "").toLowerCase().includes(q)
      );
    }

    if (filters.leave_type) {
      result = result.filter(
        (item) =>
          (item.leave_type ?? "").toString().toLowerCase() ===
          filters.leave_type.toString().toLowerCase()
      );
    }

    if (filters.status) {
      result = result.filter(
        (item) =>
          (item.status ?? "").toString().toLowerCase() ===
          filters.status.toString().toLowerCase()
      );
    }

    if (filters.dateRange?.length === 2) {
      const [start, end] = filters.dateRange;
      result = result.filter((item) => {
        const itemDate = dayjs(item.start_date);
        return (
          itemDate.isAfter(dayjs(start).subtract(1, "day")) &&
          itemDate.isBefore(dayjs(end).add(1, "day"))
        );
      });
    }

    // Organisation filter - accept both id-based and name-based matches
    if (filters.organisation) {
      result = result.filter((item) => {
        const emp = item.employee ?? {};
        const empOrgId = getEmployeeFieldId(emp, "organisation");
        const empOrgIdAlt = getEmployeeFieldId(emp, "organisation_id");
        const orgName = getEmployeeFieldName(emp, ["organisation_name", "organisation"]);
        // if filter value matches a numeric id-like value, prefer id comparison
        if (typeof filters.organisation === "number" || /^\d+$/.test(String(filters.organisation))) {
          return String(empOrgId ?? empOrgIdAlt) === String(filters.organisation);
        } else {
          // compare names (case-insensitive)
          return String(orgName).toLowerCase() === String(filters.organisation).toLowerCase();
        }
      });
    }

    // Department filter
    if (filters.department) {
      result = result.filter((item) => {
        const emp = item.employee ?? {};
        const empDeptId = getEmployeeFieldId(emp, "department");
        const empDeptAlt = getEmployeeFieldId(emp, "department_id");
        const deptName = getEmployeeFieldName(emp, ["department_name", "department"]);
        if (typeof filters.department === "number" || /^\d+$/.test(String(filters.department))) {
          return String(empDeptId ?? empDeptAlt) === String(filters.department);
        } else {
          return String(deptName).toLowerCase() === String(filters.department).toLowerCase();
        }
      });
    }

    // Designation filter
    if (filters.designation) {
      result = result.filter((item) => {
        const emp = item.employee ?? {};
        const empDesigId = getEmployeeFieldId(emp, "designation");
        const empDesigAlt = getEmployeeFieldId(emp, "designation_id");
        const desigName = getEmployeeFieldName(emp, ["designation_name", "designation"]);
        if (typeof filters.designation === "number" || /^\d+$/.test(String(filters.designation))) {
          return String(empDesigId ?? empDesigAlt) === String(filters.designation);
        } else {
          return String(desigName).toLowerCase() === String(filters.designation).toLowerCase();
        }
      });
    }

    setFilteredLeaves(result);
  }, [filters, leaves]);

  // ---------- Modal handlers ----------
  const openEditModal = (record) => {
    setSelectedLeave(record);
    setNewStatus(record.status ?? "");
    setCancelReason("");
    setIsModalOpen(true);
  };

  const downloadExcel = () => {
    // Build rows exactly matching the table columns
    const rows = filteredLeaves.map((item) => ({
      "Employee Name": `${item?.employee?.first_name ?? ""} ${item?.employee?.last_name ?? ""
        }`.trim(),
      "Employee Code": item?.employee?.emp_code ?? "-",
      Organisation:
        getEmployeeFieldName(item.employee, [
          "organisation_name",
          "organisation",
        ]) || getEmployeeFieldId(item.employee, "organisation") || "-",
      Department:
        getEmployeeFieldName(item.employee, [
          "department_name",
          "department",
        ]) || getEmployeeFieldId(item.employee, "department") || "-",
      Designation:
        getEmployeeFieldName(item.employee, [
          "designation_name",
          "designation",
        ]) || getEmployeeFieldId(item.employee, "designation") || "-",
      "Leave Type": item.leave_type ?? "-",
      Duration: item.duration_type ?? "-",
      Days: item.leave_days_count ?? "-",
      "From Date": item.start_date
        ? new Date(item.start_date).toLocaleDateString("en-IN")
        : "-",
      "To Date": item.end_date
        ? new Date(item.end_date).toLocaleDateString("en-IN")
        : "-",
      Reason: item.reason ?? "-",
      Status: item.status ?? "-",
    }));

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Leaves");

    // Download file
    const fileName = viewingRejected ? "Rejected_Leaves.xlsx" : "Employee_Leaves.xlsx";
    XLSX.writeFile(workbook, fileName);
  };


  const handleModalOk = async () => {
    if (!selectedLeave) {
      setIsModalOpen(false);
      return;
    }

    if (newStatus === "Cancelled" && !cancelReason.trim()) {
      toast.error("Please provide a cancellation reason before saving.");
      return;
    }

    try {
      // hitting cancel endpoint as in your earlier code; adjust endpoint if needed
      await axios.post(
        `${backUrl}/api/hr/leaves/${selectedLeave.id}/admin-update-status/`,
        {
          status: newStatus,
          remarks: cancelReason
        },
        {
          headers: { Authorization: `Bearer ${authData?.access}` },
        }
      );
      toast.success("Leave status updated successfully.");
      // Refresh the appropriate data based on current view
      if (viewingRejected) {
        fetchRejectedLeaves();
      } else {
        fetchLeaves();
      }
    } catch (error) {
      console.error("Error updating leave:", error);
      toast.error("Failed to update leave status.");
    } finally {
      setIsModalOpen(false);
      setSelectedLeave(null);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedLeave(null);
  };

  // ---------- Bulk Approve Handler ----------
  const handleBulkApprove = async () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }

    setBulkProcessing(true);

    try {
      const bulkApproveApi = `${backUrl}/api/hr/leaves/approve/`;

      const payload = {
        leave_ids: selectedRowKeys,
        rejection_reason: bulkRemarks
      };

      const res = await axios.post(bulkApproveApi, payload, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });

      const successMsg =
        res?.data?.message ||
        res?.data?.detail ||
        "Leaves approved successfully";

      toast.success(successMsg);

      // Reset states
      setBulkRemarks("");
      setShowBulkApprove(false);
      setSelectedRowKeys([]);

      // Refresh data
      if (viewingRejected) {
        fetchRejectedLeaves();
      } else {
        fetchLeaves();
      }

    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Bulk approval failed";
      toast.error(errorMsg);
    } finally {
      setBulkProcessing(false);
    }
  };

  // ---------- Bulk Reject Handler ----------
  const handleBulkReject = async () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }

    if (!bulkRemarks.trim()) {
      toast.warning("Please provide a reason for rejection");
      return;
    }

    setBulkProcessing(true);

    try {
      const bulkRejectApi = `${backUrl}/api/hr/leaves/reject/`;

      const payload = {
        leave_ids: selectedRowKeys,
        rejection_reason: bulkRemarks
      };

      const res = await axios.post(bulkRejectApi, payload, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });

      const successMsg =
        res?.data?.message ||
        res?.data?.detail ||
        "Leaves rejected successfully";

      toast.success(successMsg);

      // Reset states
      setBulkRemarks("");
      setShowBulkReject(false);
      setSelectedRowKeys([]);

      // Refresh data
      if (viewingRejected) {
        fetchRejectedLeaves();
      } else {
        fetchLeaves();
      }

    } catch (error) {
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Bulk rejection failed";
      toast.error(errorMsg);
    } finally {
      setBulkProcessing(false);
    }
  };

  // ---------- Row Selection Configuration ----------
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      name: record.id,
    }),
  };

  // ---------- Table columns ----------
  const columns = [
    {
      title: "Employee Code",
      dataIndex: ["employee", "emp_code"],
      key: "emp_code",
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <a
            onClick={() => {
              setSelectedId(record.employee.id);
              setShowEmp(true);
            }}
            style={{ cursor: "pointer", color: '#1890ff' }}
          >
            {text}
          </a>
        </div>
      ),
      height: 30,
      width: 180,
    },
    {
      title: "Employee Name",
      dataIndex: ["employee", "first_name"],
      key: "name",
      fixed: "left",
      render: (_, record) =>
        `${record?.employee?.first_name ?? ""} ${record?.employee?.last_name ?? ""}`.trim(),
    },
    {
      title: "Organisation",
      dataIndex: ["employee", "organisation_name"],
      key: "organisation_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, ["organisation_name", "organisation"]) ||
        getEmployeeFieldId(record.employee, "organisation") ||
        "-",
    },
    {
      title: "Department",
      dataIndex: ["employee", "department_name"],
      key: "department_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, ["department_name", "department"]) ||
        getEmployeeFieldId(record.employee, "department") ||
        "-",
    },
    {
      title: "Designation",
      dataIndex: ["employee", "designation_name"],
      key: "designation_name",
      render: (_, record) =>
        getEmployeeFieldName(record.employee, ["designation_name", "designation"]) ||
        getEmployeeFieldId(record.employee, "designation") ||
        "-",
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
    },
    {
      title: "Duration",
      dataIndex: "duration_type",
      key: "duration_type",
    },
    {
      title: "Days",
      dataIndex: "leave_days_count",
      key: "leave_days_count",
    },
    {
      title: "From Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => (date ? new Date(date).toLocaleDateString("en-IN") : "-"),
    },
    {
      title: "To Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) => (date ? new Date(date).toLocaleDateString("en-IN") : "-"),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color =
          status === "Approved"
            ? "green"
            : status === "Rejected"
              ? "red"
              : status === "Cancelled"
                ? "gray"
                : "orange";
        return <span style={{ color, fontWeight: 600 }}>{status ?? "-"}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <EditOutlined
          style={{ color: "#0e497b", fontSize: "18px", cursor: "pointer" }}
          onClick={() => openEditModal(record)}
        />
      ),
      fixed: "right",
    },
  ];

  // ---------- Render ----------
  return (
    <div style={{ padding: "20px" }}>

      {
        showEmp ? (
          <EmployeeProfile empId={selectedId} onBack={onBack} DisableEditOption={true}/>
        ) : (
          <>
            <ToastContainer />
            <Title
              level={4}
              style={{ marginBottom: 16, textTransform: "uppercase", color: "#0e497b" }}
            >
              {viewingRejected ? "Rejected Leave Records" : "Employee Leave Records"}
            </Title>

            {/* Action Buttons */}
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button
                  type={!viewingRejected ? "primary" : "default"}
                  style={
                    !viewingRejected
                      ? { background: "#0e497b", borderColor: "#0e497b" }
                      : {}
                  }
                  onClick={fetchLeaves}
                >
                  All Leaves
                </Button>
                <Button
                  type={viewingRejected ? "primary" : "default"}
                  style={
                    viewingRejected
                      ? { background: "#d32f2f", borderColor: "#d32f2f" }
                      : {}
                  }
                  onClick={fetchRejectedLeaves}
                >
                  Rejected
                </Button>
              </Space>
            </div>

            {/* Filter Section */}
            <div
              style={{
                background: "#ffffff",
                padding: "15px",
                borderRadius: "10px",
                marginBottom: "15px",
              }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Input
                    placeholder="Employee Name"
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  />
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Input
                    placeholder="Employee Code"
                    value={filters.emp_code}
                    onChange={(e) => setFilters({ ...filters, emp_code: e.target.value })}
                  />
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Select
                    placeholder="Leave Type"
                    value={filters.leave_type || undefined}
                    onChange={(value) => setFilters({ ...filters, leave_type: value })}
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {[...new Set(leaves.map((l) => l.leave_type).filter(Boolean))].map(
                      (type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      )
                    )}
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Select
                    placeholder="Status"
                    value={filters.status || undefined}
                    onChange={(value) => setFilters({ ...filters, status: value })}
                    allowClear
                    style={{ width: "100%" }}
                  >
                    <Option value="Approved">Approved</Option>
                    <Option value="Rejected">Rejected</Option>
                    <Option value="Pending">Pending</Option>
                    <Option value="Cancelled">Cancelled</Option>
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <RangePicker
                    format="DD/MM/YYYY"
                    value={filters.dateRange}
                    onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                    style={{ width: "100%" }}
                  />
                </Col>

                {/* Organisation */}
                <Col xs={24} sm={12} md={6}>
                  <Select
                    placeholder="Organisation"
                    value={filters.organisation || undefined}
                    onChange={(value) =>
                      setFilters({
                        ...filters,
                        organisation: value,
                        department: "",
                        designation: "",
                      })
                    }
                    allowClear
                    style={{ width: "100%" }}
                  >
                    {Organisations.map((org) => {
                      const key = org.id ?? org.pk ?? org.organisation ?? org.organisation_id ?? org.name;
                      const label = org.name ?? org.title ?? org.organisation_name ?? String(key);
                      return (
                        <Option key={key} value={key}>
                          {label}
                        </Option>
                      );
                    })}
                  </Select>
                </Col>

                {/* Department (depends on organisation) */}
                <Col xs={24} sm={12} md={6}>
                  <Select
                    placeholder="Department"
                    value={filters.department || undefined}
                    onChange={(value) =>
                      setFilters({ ...filters, department: value, designation: "" })
                    }
                    allowClear
                    style={{ width: "100%" }}
                    disabled={!filters.organisation}
                  >
                    {departments.map((dept) => {
                      const key = dept.id ?? dept.pk ?? dept.department ?? dept.department_id ?? dept.name;
                      const label = dept.name ?? dept.title ?? dept.department_name ?? String(key);
                      return (
                        <Option key={key} value={key}>
                          {label}
                        </Option>
                      );
                    })}
                  </Select>
                </Col>

                {/* Designation (depends on department) */}
                <Col xs={24} sm={12} md={6}>
                  <Select
                    placeholder="Designation"
                    value={filters.designation || undefined}
                    onChange={(value) => setFilters({ ...filters, designation: value })}
                    allowClear
                    style={{ width: "100%" }}
                    disabled={!filters.department}
                  >
                    {designations.map((des) => {
                      const key = des.id ?? des.pk ?? des.designation ?? des.designation_id ?? des.name;
                      const label = des.name ?? des.title ?? des.designation_name ?? String(key);
                      return (
                        <Option key={key} value={key}>
                          {label}
                        </Option>
                      );
                    })}
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Button
                    type="primary"
                    style={{ background: "#0e497b", borderColor: "#0e497b", width: "100%" }}
                    onClick={downloadExcel}
                  >
                    Download Excel
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Bulk Action Buttons */}
            {selectedRowKeys.length > 0 && (
              <div style={{ marginBottom: 16, textAlign: "center" }}>
                <Space>
                  <Tag color="blue">{selectedRowKeys.length} selected</Tag>
                  {/* <Button
              type="primary"
              icon={<CheckOutlined />}
              style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
              onClick={() => setShowBulkApprove(true)}
            >
              Bulk Approve
            </Button>
            <Button
              danger
              type="primary"
              icon={<CloseOutlined />}
              onClick={() => setShowBulkReject(true)}
            >
              Bulk Reject
            </Button> */}
                  <Button onClick={() => setSelectedRowKeys([])}>
                    Clear Selection
                  </Button>
                </Space>
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <Spin size="large" />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table
                  columns={columns}
                  dataSource={filteredLeaves}
                  rowKey={(record) => record.id}
                  rowSelection={rowSelection}
                  bordered
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: "max-content" }}
                  components={{
                    header: {
                      cell: (props) => (
                        <th
                          {...props}
                          style={{
                            backgroundColor: "#0e497b",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {props.children}
                        </th>
                      ),
                    },
                  }}
                />
              </div>
            )}

            {/* Edit Modal */}
            <Modal
              title="Edit Leave Status"
              open={isModalOpen}
              onOk={handleModalOk}
              onCancel={handleModalCancel}
              okText="Save"
              centered
              okButtonProps={{
                style: {
                  backgroundColor: "#0e497b",
                  borderColor: "#0e497b",
                  color: "#fff",
                },
              }}
            >
              <p>
                <strong>Employee:</strong> {selectedLeave?.employee?.first_name} (
                {selectedLeave?.employee?.emp_code})
              </p>

              <Select
                value={newStatus}
                onChange={(value) => setNewStatus(value)}
                style={{ width: "100%", marginBottom: 12 }}
              >
                <Option value="Rejected">Rejected</Option>
              </Select>


              <Input.TextArea
                placeholder="Enter reason for cancellation"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                style={{
                  borderColor: cancelReason.trim() ? "#d9d9d9" : "red",
                }}
              />
            </Modal>

            {/* Bulk Approve Modal */}
            <Modal
              title={`Bulk Approve (${selectedRowKeys.length} leaves)`}
              open={showBulkApprove}
              footer={null}
              onCancel={() => {
                setShowBulkApprove(false);
                setBulkRemarks("");
              }}
              width={600}
              centered
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter common remarks for all selected leaves (optional)..."
                value={bulkRemarks}
                onChange={(e) => setBulkRemarks(e.target.value)}
                style={{ borderRadius: "8px", marginBottom: "16px" }}
              />
              <div style={{ textAlign: "right" }}>
                <Button
                  style={{ marginRight: "8px" }}
                  onClick={() => {
                    setShowBulkApprove(false);
                    setBulkRemarks("");
                  }}
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={bulkProcessing}
                  disabled={bulkProcessing}
                  style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                  onClick={handleBulkApprove}
                >
                  {bulkProcessing ? "Approving..." : "Approve All"}
                </Button>
              </div>
            </Modal>

            {/* Bulk Reject Modal */}
            <Modal
              title={`Bulk Reject (${selectedRowKeys.length} leaves)`}
              open={showBulkReject}
              footer={null}
              onCancel={() => {
                setShowBulkReject(false);
                setBulkRemarks("");
              }}
              width={600}
              centered
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter common rejection reason for all selected leaves..."
                value={bulkRemarks}
                onChange={(e) => setBulkRemarks(e.target.value)}
                style={{ borderRadius: "8px", marginBottom: "16px" }}
              />
              <div style={{ textAlign: "right" }}>
                <Button
                  style={{ marginRight: "8px" }}
                  onClick={() => {
                    setShowBulkReject(false);
                    setBulkRemarks("");
                  }}
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  danger
                  loading={bulkProcessing}
                  disabled={bulkProcessing || !bulkRemarks.trim()}
                  onClick={handleBulkReject}
                >
                  {bulkProcessing ? "Rejecting..." : "Reject All"}
                </Button>
              </div>
            </Modal>
          </>
        )
      }
    </div>
  );
}